import { id3v2Length, isConformant, sniffMp3Format } from './mp3Header'
import {
  TARGET_CHANNELS,
  TARGET_SAMPLE_RATE,
  type CbrKbps,
  type EncodeRequest,
  type Mp3FormatInfo,
  type WorkerResponse,
} from './types'

type PoolEntry = { worker: Worker; jobs: number }

type PendingJob = {
  resolve: (mp3: Blob) => void
  reject: (err: Error) => void
  onProgress?: (percent: number) => void
  entry: PoolEntry
}

const MAX_ENCODE_WORKERS = 3

let pool: PoolEntry[] = []
let nextJobId = 1
const pending = new Map<number, PendingJob>()

// Leave a core for the main thread; the wasm encode pegs a thread while
// it runs.
function poolLimit(): number {
  const cores = navigator.hardwareConcurrency || 4
  return Math.max(1, Math.min(MAX_ENCODE_WORKERS, cores - 1))
}

function handleMessage(event: MessageEvent<WorkerResponse>): void {
  const msg = event.data
  const job = pending.get(msg.jobId)
  if (!job) return
  if (msg.type === 'progress') {
    job.onProgress?.(msg.percent)
  } else if (msg.type === 'done') {
    pending.delete(msg.jobId)
    job.entry.jobs--
    job.resolve(new Blob([msg.mp3], { type: 'audio/mpeg' }))
  } else {
    pending.delete(msg.jobId)
    job.entry.jobs--
    job.reject(new Error(msg.message))
  }
}

// Each worker owns an independent wasm LAME instance, so jobs dispatched
// to different workers encode in parallel; jobs sent to the same worker
// still serialize inside it.
function getWorker(): PoolEntry {
  const idle = pool.find((e) => e.jobs === 0)
  if (idle) return idle
  if (pool.length < poolLimit()) {
    const worker = new Worker(new URL('./audioWorker.ts', import.meta.url), {
      type: 'module',
    })
    worker.addEventListener('message', handleMessage)
    const entry: PoolEntry = { worker, jobs: 0 }
    pool.push(entry)
    return entry
  }
  return pool.reduce((least, e) => (e.jobs < least.jobs ? e : least))
}

async function decodeAndResample(file: File): Promise<Float32Array> {
  const sourceBuf = await file.arrayBuffer()
  const ac = new AudioContext()
  let decoded: AudioBuffer
  try {
    decoded = await ac.decodeAudioData(sourceBuf)
  } finally {
    ac.close()
  }
  const length = Math.max(1, Math.ceil(decoded.duration * TARGET_SAMPLE_RATE))
  const offline = new OfflineAudioContext(TARGET_CHANNELS, length, TARGET_SAMPLE_RATE)
  const src = offline.createBufferSource()
  src.buffer = decoded
  src.connect(offline.destination)
  src.start(0)
  const rendered = await offline.startRendering()
  return rendered.getChannelData(0).slice()
}

export type EncodeOutcome = {
  blob: Blob
  skippedEncode: boolean
}

export type EncodeOptions = {
  onProgress?: (percent: number) => void
  // Override the encoder's CBR bitrate. Falls back to TARGET_KBPS inside
  // the worker if absent.
  targetKbps?: CbrKbps
}

// Sniff the source MP3 header, seeking past any ID3v2 tag first so we
// don't get tripped up by podcasts with large embedded artwork. Returns
// null for non-MP3 sources or files whose first audio frame isn't within
// 8 KB of the tag's end.
export async function sniffSourceFormat(file: File): Promise<Mp3FormatInfo | null> {
  const ext = file.name.toLowerCase().split('.').pop()
  if (ext !== 'mp3') return null
  const head10 = new Uint8Array(await file.slice(0, 10).arrayBuffer())
  const tagEnd = id3v2Length(head10)
  const sliceEnd = Math.min(file.size, tagEnd + 8192)
  if (sliceEnd <= tagEnd) return null
  const frameBuf = new Uint8Array(await file.slice(tagEnd, sliceEnd).arrayBuffer())
  return sniffMp3Format(frameBuf)
}

function formatMb(bytes: number): string {
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

export async function encodeToMp3(
  file: File,
  options: EncodeOptions = {},
): Promise<EncodeOutcome> {
  const { onProgress, targetKbps } = options
  const startedAt = performance.now()
  console.log(`[mp3-encode] start ${file.name} — ${formatMb(file.size)}`)
  const ext = file.name.toLowerCase().split('.').pop()
  if (ext === 'mp3') {
    const head = new Uint8Array(await file.slice(0, 8192).arrayBuffer())
    const info = sniffMp3Format(head)
    if (info && isConformant(info)) {
      onProgress?.(100)
      const seconds = ((performance.now() - startedAt) / 1000).toFixed(1)
      console.log(
        `[mp3-encode] end ${file.name} — ${formatMb(file.size)} in ${seconds}s (already conformant, encode skipped)`,
      )
      return { blob: file, skippedEncode: true }
    }
  }

  const pcm = await decodeAndResample(file)
  const entry = getWorker()
  const jobId = nextJobId++

  const blob = await new Promise<Blob>((resolve, reject) => {
    entry.jobs++
    pending.set(jobId, { resolve, reject, onProgress, entry })
    const copy = new Float32Array(pcm)
    const pcmBuf = copy.buffer as ArrayBuffer
    const req: EncodeRequest = {
      type: 'encode',
      jobId,
      pcm: pcmBuf,
      targetKbps,
    }
    entry.worker.postMessage(req, [pcmBuf])
  })

  const seconds = ((performance.now() - startedAt) / 1000).toFixed(1)
  console.log(
    `[mp3-encode] end ${file.name} — ${formatMb(file.size)} → ${formatMb(blob.size)} in ${seconds}s`,
  )
  return { blob, skippedEncode: false }
}

export function disposeWorker(): void {
  for (const { worker } of pool) worker.terminate()
  pool = []
  pending.clear()
}
